import { Button } from '@/components/ui/button';
import { useIngestarBatch } from '@/hooks/useIngestarBatch';
import { cn } from '@/lib/utils';
import { FileUp, Loader2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

const TAM_MAXIMO_BYTES = 50 * 1024 * 1024; // 50 MB

interface Props {
  onSubiendoCambio?: (subiendo: boolean) => void;
}

/**
 * Zona de subida de CSV de planta Blend.
 *
 * - Drag & drop nativo + botón "Seleccionar archivo".
 * - Validación cliente: extensión `.csv` y tamaño <= 50MB.
 * - Lee con `await file.text()` (UTF-8) y dispara `useIngestarBatch().mutate(...)`.
 * - Mientras la mutation está pending, el botón muestra spinner; el resto de la
 *   pantalla no se bloquea.
 * - Toasts en `useIngestarBatch.onSuccess` / `onError` (resúmenes y errores).
 */
export function UploadCsvBlend({ onSubiendoCambio }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const { mutate, isPending } = useIngestarBatch();

  const notificarCambio = (sub: boolean) => {
    onSubiendoCambio?.(sub);
  };

  const validarYProcesar = async (archivo: File) => {
    if (!archivo.name.toLowerCase().endsWith('.csv')) {
      toast.error('Solo se aceptan archivos .csv', {
        description: `Recibido: ${archivo.name}`,
      });
      return;
    }
    if (archivo.size > TAM_MAXIMO_BYTES) {
      const mb = Math.round(archivo.size / 1024 / 1024);
      toast.error(`Archivo demasiado grande (${mb} MB). Máximo 50 MB.`);
      return;
    }

    let contenido: string;
    try {
      contenido = await archivo.text();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'desconocido';
      toast.error(`No se pudo leer el archivo: ${msg}`);
      return;
    }

    notificarCambio(true);
    mutate(
      { nombre_archivo: archivo.name, contenido_csv: contenido, forzar_reingesta: false },
      { onSettled: () => notificarCambio(false) },
    );
  };

  const onClickSeleccionar = () => {
    inputRef.current?.click();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    void validarYProcesar(archivo);
    // Reset para que seleccionar el MISMO archivo dos veces vuelva a disparar.
    e.target.value = '';
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setArrastrando(true);
  };
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setArrastrando(false);
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setArrastrando(false);
    const archivo = e.dataTransfer.files?.[0];
    if (!archivo) return;
    void validarYProcesar(archivo);
  };

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed p-8 transition-colors',
        arrastrando ? 'border-adelante-primary bg-adelante-primary/5' : 'border-border bg-muted/30',
      )}
    >
      <FileUp className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
      <div className="text-center">
        <p className="text-sm font-medium">Arrastrá un CSV o seleccionalo</p>
        <p className="text-xs text-muted-foreground">
          Solo archivos `.csv` de la planta Blend, máximo 50 MB.
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={onInputChange}
        data-testid="input-archivo"
      />

      <Button onClick={onClickSeleccionar} disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Procesando…
          </>
        ) : (
          <>
            <Upload className="mr-2 h-4 w-4" />
            Seleccionar archivo
          </>
        )}
      </Button>
    </div>
  );
}
